// Copyright (c) 2025 IOTA Stiftung
// SPDX-License-Identifier: Apache-2.0

/* tslint:disable */
/* oxlint-disable */
// @ts-nocheck
/**
 * Ampli - A strong typed wrapper for your Analytics
 *
 * This file is generated by Amplitude.
 * To update run 'ampli pull web'
 *
 * Required dependencies: @amplitude/analytics-browser@^1.3.0
 * Tracking Plan Version: 1
 * Build: 1.0.0
 * Runtime: browser:typescript-ampli-v2
 *
 * [View Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20EVM%20Bridge/events/main/latest)
 *
 * [Full Setup Instructions](https://data.eu.amplitude.com/iota-foundation/IOTA%20EVM%20Bridge/implementation/web)
 */

import * as amplitude from '@amplitude/analytics-browser';
import { getAmplitudeConsentStatus } from '@iota/core';

export type Environment = 'iotaevmbridge';

export const ApiKey: Record<Environment, string> = {
    iotaevmbridge: 'bc860617cd112db8797d4b8809b15142',
};

/**
 * Default Amplitude configuration options. Contains tracking plan information.
 */
export const DefaultConfiguration: BrowserOptions = {
    plan: {
        version: '1',
        branch: 'main',
        source: 'web',
        versionId: '57f759dd-f594-4c8d-8f19-d4a3e07129e8',
    },
    ...{
        ingestionMetadata: {
            sourceName: 'browser-typescript-ampli',
            sourceVersion: '2.0.0',
        },
    },
    serverZone: amplitude.Types.ServerZone.EU,
};

export interface LoadOptionsBase {
    disabled?: boolean;
}

export type LoadOptionsWithEnvironment = LoadOptionsBase & {
    environment: Environment;
    client?: { configuration?: BrowserOptions };
};
export type LoadOptionsWithApiKey = LoadOptionsBase & {
    client: { apiKey: string; configuration?: BrowserOptions };
};
export type LoadOptionsWithClientInstance = LoadOptionsBase & {
    client: { instance: BrowserClient };
};

export type LoadOptions =
    | LoadOptionsWithEnvironment
    | LoadOptionsWithApiKey
    | LoadOptionsWithClientInstance;

export interface ClickedMaxAmountProperties {
    bridgeDirection: string;
    coinType: string;
}

export interface ConnectedL1WalletProperties {
    walletType: string;
}

export interface ConnectedL2WalletProperties {
    chainId: string;
    walletType: string;
}

export interface SelectedCoinProperties {
    bridgeDirection: string;
    coinSymbol: string;
    coinType: string;
}

export interface SentFromL1ToL2Properties {
    amount: string;
    coinType: string;
}

export interface SentFromL2ToL1Properties {
    amount: string;
    coinType: string;
}

export interface ToggledAddressInputProperties {
    bridgeDirection: string;
    inputMode: string;
}

export interface ToggledBridgeDirectionProperties {
    bridgeDirection: string;
}

export class ClickedMaxAmount implements BaseEvent {
    event_type = 'clicked max amount';

    constructor(public event_properties: ClickedMaxAmountProperties) {
        this.event_properties = event_properties;
    }
}

export class ConnectedL1Wallet implements BaseEvent {
    event_type = 'connected l1 wallet';

    constructor(public event_properties: ConnectedL1WalletProperties) {
        this.event_properties = event_properties;
    }
}

export class ConnectedL2Wallet implements BaseEvent {
    event_type = 'connected l2 wallet';

    constructor(public event_properties: ConnectedL2WalletProperties) {
        this.event_properties = event_properties;
    }
}

export class RequestedFaucetFunds implements BaseEvent {
    event_type = 'requested faucet funds';
}

export class SelectedCoin implements BaseEvent {
    event_type = 'selected coin';

    constructor(public event_properties: SelectedCoinProperties) {
        this.event_properties = event_properties;
    }
}

export class SentFromL1ToL2 implements BaseEvent {
    event_type = 'sent from l1 to l2';

    constructor(public event_properties: SentFromL1ToL2Properties) {
        this.event_properties = event_properties;
    }
}

export class SentFromL2ToL1 implements BaseEvent {
    event_type = 'sent from l2 to l1';

    constructor(public event_properties: SentFromL2ToL1Properties) {
        this.event_properties = event_properties;
    }
}

export class ToggledAddressInput implements BaseEvent {
    event_type = 'toggled address input';

    constructor(public event_properties: ToggledAddressInputProperties) {
        this.event_properties = event_properties;
    }
}

export class ToggledBridgeDirection implements BaseEvent {
    event_type = 'toggled bridge direction';

    constructor(public event_properties: ToggledBridgeDirectionProperties) {
        this.event_properties = event_properties;
    }
}

export type PromiseResult<T> = { promise: Promise<T | void> };

const getVoidPromiseResult = () => ({ promise: Promise.resolve() });

// prettier-ignore
export class Ampli {
  private disabled: boolean = false;
  private amplitude?: BrowserClient;

  get client(): BrowserClient {
    this.isInitializedAndEnabled();
    return this.amplitude!;
  }

  get isLoaded(): boolean {
    return this.amplitude != null;
  }

  private isInitializedAndEnabled(): boolean {

    // NOTE don't show error if consent is not given yet.
    // Don't remove this check after `ampli pull web`
    if (getAmplitudeConsentStatus() === 'declined') return;

    if (!this.amplitude) {
      console.error('ERROR: Ampli is not yet initialized. Have you called ampli.load() on app start?');
      return false;
    }
    return !this.disabled;
  }

  /**
   * Initialize the Ampli SDK. Call once when your application starts.
   *
   * @param options Configuration options to initialize the Ampli SDK with.
   */
  load(options: LoadOptions): PromiseResult<void> {
    this.disabled = options.disabled ?? false;

    if (this.amplitude) {
      console.warn('WARNING: Ampli is already initialized. Ampli.load() should be called once at application startup.');
      return getVoidPromiseResult();
    }

    let apiKey: string | null = null;
    if (options.client && 'apiKey' in options.client) {
      apiKey = options.client.apiKey;
    } else if ('environment' in options) {
      apiKey = ApiKey[options.environment];
    }

    if (options.client && 'instance' in options.client) {
      this.amplitude = options.client.instance;
    } else if (apiKey) {
      this.amplitude = amplitude.createInstance();
      const configuration = (options.client && 'configuration' in options.client) ? options.client.configuration : {};
      return this.amplitude.init(apiKey, undefined, { ...DefaultConfiguration, ...configuration });
    } else {
      console.error("ERROR: ampli.load() requires 'environment', 'client.apiKey', or 'client.instance'");
    }

    return getVoidPromiseResult();
  }

  /**
   * Identify a user and set user properties.
   *
   * @param userId The user's id.
   * @param options Optional event options.
   */
  identify(
    userId: string | undefined,
    options?: EventOptions,
  ): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    if (userId) {
      options = {...options,  user_id: userId};
    }

    const amplitudeIdentify = new amplitude.Identify();
    return this.amplitude!.identify(
      amplitudeIdentify,
      options,
    );
  }

 /**
  * Flush the event.
  */
  flush() : PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    return this.amplitude!.flush();
  }

  /**
   * Track event
   *
   * @param event The event to track.
   * @param options Optional event options.
   */
  track(event: Event, options?: EventOptions): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    return this.amplitude!.track(event, undefined, options);
  }

  /**
   * clicked max amount
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20EVM%20Bridge/events/main/latest/clicked%20max%20amount)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. bridgeDirection)
   * @param options Amplitude event options.
   */
  clickedMaxAmount(
    properties: ClickedMaxAmountProperties,
    options?: EventOptions,
  ) {
    return this.track(new ClickedMaxAmount(properties), options);
  }

  /**
   * connected l1 wallet
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20EVM%20Bridge/events/main/latest/connected%20l1%20wallet)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. walletType)
   * @param options Amplitude event options.
   */
  connectedL1Wallet(
    properties: ConnectedL1WalletProperties,
    options?: EventOptions,
  ) {
    return this.track(new ConnectedL1Wallet(properties), options);
  }

  /**
   * connected l2 wallet
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20EVM%20Bridge/events/main/latest/connected%20l2%20wallet)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. chainId)
   * @param options Amplitude event options.
   */
  connectedL2Wallet(
    properties: ConnectedL2WalletProperties,
    options?: EventOptions,
  ) {
    return this.track(new ConnectedL2Wallet(properties), options);
  }

  /**
   * requested faucet funds
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20EVM%20Bridge/events/main/latest/requested%20faucet%20funds)
   *
   * Event has no description in tracking plan.
   *
   * @param options Amplitude event options.
   */
  requestedFaucetFunds(
    options?: EventOptions,
  ) {
    return this.track(new RequestedFaucetFunds(), options);
  }

  /**
   * selected coin
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20EVM%20Bridge/events/main/latest/selected%20coin)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. bridgeDirection)
   * @param options Amplitude event options.
   */
  selectedCoin(
    properties: SelectedCoinProperties,
    options?: EventOptions,
  ) {
    return this.track(new SelectedCoin(properties), options);
  }

  /**
   * sent from l1 to l2
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20EVM%20Bridge/events/main/latest/sent%20from%20l1%20to%20l2)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. amount)
   * @param options Amplitude event options.
   */
  sentFromL1ToL2(
    properties: SentFromL1ToL2Properties,
    options?: EventOptions,
  ) {
    return this.track(new SentFromL1ToL2(properties), options);
  }

  /**
   * sent from l2 to l1
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20EVM%20Bridge/events/main/latest/sent%20from%20l2%20to%20l1)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. amount)
   * @param options Amplitude event options.
   */
  sentFromL2ToL1(
    properties: SentFromL2ToL1Properties,
    options?: EventOptions,
  ) {
    return this.track(new SentFromL2ToL1(properties), options);
  }

  /**
   * toggled address input
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20EVM%20Bridge/events/main/latest/toggled%20address%20input)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. bridgeDirection)
   * @param options Amplitude event options.
   */
  toggledAddressInput(
    properties: ToggledAddressInputProperties,
    options?: EventOptions,
  ) {
    return this.track(new ToggledAddressInput(properties), options);
  }

  /**
   * toggled bridge direction
   *
   * [View in Tracking Plan](https://data.eu.amplitude.com/iota-foundation/IOTA%20EVM%20Bridge/events/main/latest/toggled%20bridge%20direction)
   *
   * Event has no description in tracking plan.
   *
   * @param properties The event's properties (e.g. bridgeDirection)
   * @param options Amplitude event options.
   */
  toggledBridgeDirection(
    properties: ToggledBridgeDirectionProperties,
    options?: EventOptions,
  ) {
    return this.track(new ToggledBridgeDirection(properties), options);
  }
}

export const ampli = new Ampli();

// BASE TYPES
type BrowserOptions = amplitude.Types.BrowserOptions;

export type BrowserClient = amplitude.Types.BrowserClient;
export type BaseEvent = amplitude.Types.BaseEvent;
export type IdentifyEvent = amplitude.Types.IdentifyEvent;
export type GroupEvent = amplitude.Types.GroupIdentifyEvent;
export type Event = amplitude.Types.Event;
export type EventOptions = amplitude.Types.EventOptions;
export type Result = amplitude.Types.Result;
